import React, { useEffect, useRef, useState } from "react";
import { storyblokEditable } from "@storyblok/react";

import { FeatureTablePanelModel } from "./FeatureTablePanelModel";
import { ProductModel } from "../ShowcasePanel/ShowcasePanelModel";
import SliderPanelUtils from "../SliderPanel/SliderPanelUtils";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import Utils from "../../core/Utils/Utils";
import Button from "../../core/Button/Button";

const FeatureTablePanelMobile: React.FC<{ blok: FeatureTablePanelModel }> = ({
  blok,
}) => {
  const [isChecked, setIsChecked] = useState(false);
  const [displayAnnuallyOrMonthlySwitch, setDisplayAnnuallyOrMonthlySwitch] =
    useState(false);
  const [extendedRows, setExtendedRows] = useState<Record<number, boolean>>({});
  const isVideo = blok.media?.filename?.endsWith(".mp4");
  const carouselRef = useRef<any>(null);
  const handleToggle = () => {
    setIsChecked(!isChecked);
  };

  useEffect(() => {
    if (blok?.entity?.some((e) => e.content?.productType === "Subscription")) {
      setDisplayAnnuallyOrMonthlySwitch(true);
    }
    setIsChecked(blok?.defaultSelection === "Monthly");
    if (carouselRef.current && blok.defaultSelectedTier) {
      const tierIndex = blok.items.findIndex(
        (X) => X.tier === blok.defaultSelectedTier
      );
      carouselRef.current.goToSlide(tierIndex > -1 ? tierIndex : 0);
    }
  }, [blok]);

  if (!blok?.entity || blok.entity.length !== 1) {
    return null;
  }
  const productEntity = blok.entity[0]?.content as ProductModel;

  const responsive = {
    mobile: {
      breakpoint: { max: 768, min: 0 },
      items: 1,
    },
  };

  return (
    <div
      {...storyblokEditable(blok)}
      className="feature-table-panel-container-mobile"
      style={{ color: blok.textColor }}
    >
      {isVideo && (
        <video
          autoPlay
          loop
          muted
          playsInline
          src={Utils.getCacheUrl(blok.media?.filename)}
          data-object-fit="cover"
          onError={(e) => Utils.handleVideoError(e, blok.media.filename)}
        ></video>
      )}
      {!isVideo && blok.media?.filename && (
        <img
          src={Utils.getCacheUrl(blok.media.filename)}
          alt=""
          onError={(e) => Utils.handleImageError(e, blok.media?.filename)}
        />
      )}

      {blok.title && (
        <div className="title">
          <span>{blok.title}</span>
        </div>
      )}
      <div className="standfirst">
        <span>{blok.standfirst}</span>
      </div>

      <div className="feature-table">
        <Carousel
          ref={carouselRef}
          responsive={responsive}
          swipeable={true}
          draggable={true}
          showDots={true}
          infinite={false}
          autoPlay={false}
        >
          {blok.items.map((item, index) => {
            const subscriptionIndex = SliderPanelUtils.getSubscriptionIndex(
              productEntity,
              item.tier
            );
            const monthlyValue =
              subscriptionIndex &&
              productEntity?.subscriptionIntervalPrice?.tbody &&
              productEntity?.subscriptionIntervalPrice.tbody.length > 0
                ? productEntity.subscriptionIntervalPrice.tbody[0]?.body[
                    subscriptionIndex
                  ]?.value
                : null;
            const monthlyFormattedValue = monthlyValue
              ? `$${monthlyValue}/month`
              : "";
            const annuallyValue =
              subscriptionIndex &&
              productEntity?.subscriptionIntervalPrice?.tbody &&
              productEntity.subscriptionIntervalPrice.tbody.length > 2
                ? productEntity.subscriptionIntervalPrice.tbody[2]?.body[
                    subscriptionIndex
                  ]?.value
                : null;

            const annuallyFormattedValue = annuallyValue
              ? `Billed yearly $${annuallyValue}/year`
              : "";
            return (
              <div key={index}>
                <div
                  className="feature-table-product"
                  style={{ borderColor: blok.textColor }}
                  key={`feature-table-product-${productEntity.productName}-${index}`}
                >
                  <div className="cell">{productEntity.productName}</div>
                  <div className="cell">{item.tier}</div>
                </div>

                <div className="row header">
                  <div className="cell">{monthlyFormattedValue}</div>
                </div>

                {!isChecked && (
                  <div className="row header">
                    <div className="cell">{annuallyFormattedValue}</div>
                  </div>
                )}

                <div className="row buttons">
                  <div className="cell">
                    {item.buttons.map((button) => {
                      if (displayAnnuallyOrMonthlySwitch) {
                        if (isChecked && button.displayFor === "annual") {
                          return null;
                        }
                        if (!isChecked && button.displayFor === "month") {
                          return null;
                        }
                      }
                      return (
                        <div
                          className="cell"
                          key={`div-${button._uid}-${item._uid}`}
                        >
                          <Button
                            blok={button}
                            key={`${button._uid}-${item._uid}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>

                {productEntity.featureList?.tbody.map((tbody, index) => {
                  const text = tbody.body[0].value;
                  const moreInfoIndex = SliderPanelUtils.getFeatureIndex(
                    productEntity,
                    "More Info"
                  );
                  const featureIndex = SliderPanelUtils.getFeatureIndex(
                    productEntity,
                    item.tier
                  );
                  const featureValue = tbody.body[featureIndex].value;
                  return (
                    <div
                      key={`feature-table-description-${blok._uid}-${index}`}
                      style={{
                        backgroundColor:
                          index % 2 === 0
                            ? blok?.tableEvenRowsColor
                            : blok?.tableOddRowsColor,
                        opacity: blok.opacity,
                      }}
                    >
                      <div
                        className="row"
                        onClick={() => {
                          if (tbody.body[moreInfoIndex].value) {
                            setExtendedRows((prev) => ({
                              ...prev,
                              [index]: !prev[index],
                            }));
                          }
                        }}
                      >
                        <div
                          className={`cell text ${
                            tbody.body[moreInfoIndex].value
                              ? extendedRows[index]
                                ? "arrow-up"
                                : "arrow-down"
                              : ""
                          }`}
                          title={text}
                        >
                          {text}
                        </div>
                        <div
                          className="cell feature-cell"
                          title={featureValue === "x" ? "" : featureValue}
                        >
                          {featureValue === "x" ? "" : featureValue}
                        </div>
                      </div>
                      <div
                        className={`more-info ${
                          extendedRows[index] ? "extended" : ""
                        }`}
                        style={{
                          color: blok.extendedRowTextColor,
                          backgroundColor: blok.extendedRowBackgroundColor,
                          opacity: blok.opacity,
                        }}
                      >
                        {tbody.body[moreInfoIndex].value}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </Carousel>
      </div>
      {displayAnnuallyOrMonthlySwitch && (
        <div className="switch-container">
          <div className="switch-contrtol">
            <span className="slider-label">Annually</span>
            <label className="switch">
              <input
                type="checkbox"
                checked={isChecked}
                onChange={handleToggle}
              />
              <span className="slider"></span>
            </label>
            <span className="slider-label">Monthly</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeatureTablePanelMobile;
